// src/pages/ProductPassport.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchProductPassport } from '../../apiService';
import { getAuthHeaders } from '../../hydraApiService';
import {
  Info,
  Settings,
  Leaf,
  RotateCw,
  Recycle,
  Package,
  Shield,
  Tv,
  Thermometer,
  AlertTriangle,
  CheckCircle,
  Loader,
  Edit2,
  Eye,
  ChevronDown,
  ChevronUp,
  Download,
  Share2,
  MapPin,
} from 'lucide-react';
import Navbar from '../../components/navbar';
import FieldBox from './Fieldbox';
import TraceabilityTimeline from '../../Dashboard/TraceabilityTimeline';
import LifecycleEventsList from '../../components/LifeCycleEvent/LifecycleEventsList';
import {
  apiToTabMapping,
  flattenCategoryData,
  sortFields,
  fieldOrderMap,
  formatLabel,
  formatValue,
  isURL,
  isNestedObject,
  isArrayOfObjects,
  tryParseJSON
} from './Productpassporthelpers';

// ── Tab definitions ───────────────────────────────────────────────────────────
const allTabDefinitions = {
  identification:       { label: 'Identification & Metadata',   icon: Info },
  productInfo:          { label: 'Product Information',          icon: Package },
  durability:           { label: 'Durability & Repair',          icon: Settings },
  warranty:             { label: 'Warranty Information',         icon: Shield },
  materialComposition:  { label: 'Material Composition',         icon: Package },
  substancesOfConcern:  { label: 'Substances of Concern',        icon: AlertTriangle },
  economicOperators:    { label: 'Economic Operators',           icon: Shield },
  sustainability:       { label: 'Sustainability',               icon: Leaf },
  environmental:        { label: 'Environmental Data',           icon: Leaf },
  tvSpecs:              { label: 'Television Specifications',    icon: Tv },
  operatingSoftware:    { label: 'Operating & Software',         icon: Thermometer },
  safetyConsumer:       { label: 'Safety & Consumer Info',       icon: AlertTriangle },
  endoflife:            { label: 'End of Life',                  icon: Recycle },
  lifecycleCompliance:  { label: 'Lifecycle & Compliance',       icon: RotateCw },
  traceabilityEvents:   { label: 'Traceability Events',          icon: MapPin },
};

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

// ─────────────────────────────────────────────────────────────────────────────
function ProductPassport() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState('identification');
  const [isRightSidebarExpanded, setIsRightSidebarExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productData, setProductData] = useState(null);
  const [dppId, setDppId] = useState(null);
  const [gtin, setGtin] = useState(null);
  const [transactionalData, setTransactionalData] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveNotification, setSaveNotification] = useState(null);
  const [visibleFields, setVisibleFields] = useState(null); // null = show all

  const TRANSACTIONAL_KEYS = ['dpp_created_by', 'data_carrier_type', 'link_to_digital_copy', 'ownership', 'passport_availability_period'];

  const getUserRole = () => {
    try {
      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
      if (!token) return 'Manufacturer';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.type || payload.role || 'Manufacturer';
    } catch { return 'Manufacturer'; }
  };

  const userRole = getUserRole();

  // ── Fetch transactional field visibility ────────────────────────────────────
  useEffect(() => {
    if (userRole === 'Manufacturer') return;
    const fetchRoleAccess = async () => {
      try {
        const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/role-access`, {
          headers: { 'Content-Type': 'application/json', 'X-User-Role': userRole, Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success && data.data[userRole]) {
          const roleData = data.data[userRole];
          setVisibleFields(TRANSACTIONAL_KEYS.filter(k => roleData[k]?.isVisible));
        } else {
          setVisibleFields([]);
        }
      } catch (err) {
        console.error('Role access fetch error:', err);
        setVisibleFields(null);
      }
    };
    fetchRoleAccess();
  }, [userRole]);

  // ── Data fetching ───────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchProductPassport(id);
        if (result.success && result.data) {
          const { dppId: resDppId, gtin: resGtin, transactional, masterData } = result.data;
          setDppId(resDppId);
          setGtin(resGtin);
          setTransactionalData(transactional);
          setProductData(masterData);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching product passport:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProductData();
  }, [id]);

  useEffect(() => {
    if (saveNotification) {
      const timer = setTimeout(() => setSaveNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveNotification]);

  // ── Tab helpers ─────────────────────────────────────────────────────────────
  function getAvailableTabs() {
    if (!productData?.categories) return [];

    const availableTabIds = [...new Set(
      Object.keys(productData.categories)
        .map(k => apiToTabMapping[k])
        .filter(Boolean)
        .filter(tabId => allTabDefinitions[tabId])
    )];

    const tabs = availableTabIds.map(tabId => ({ id: tabId, ...allTabDefinitions[tabId] }));

    // Always inject Traceability Events tab
    const events = transactionalData?.traceability_events;
    if (Array.isArray(events)) {
      tabs.push({ id: 'traceabilityEvents', ...allTabDefinitions.traceabilityEvents });
    }

    return tabs;
  }

  const tabs = getAvailableTabs();

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs.length]);

  function getFieldsForTab(tabId) {
    if (tabId === 'traceabilityEvents') return [];
    if (!productData?.categories) return [];
    const apiKeys = Object.keys(apiToTabMapping).filter(k => apiToTabMapping[k] === tabId);
    if (apiKeys.length === 0) return [];

    const editableCategories = productData?.editableCategories || [];
    const isTabEditable = apiKeys.some(k => editableCategories.includes(k));

    const fields = [];

    if (tabId === 'identification' && dppId) {
      fields.push({
        label: 'DPP ID', value: dppId,
        isNested: false, apiKey: null, fieldKey: 'dppId', isEditable: false
      });
    }

    apiKeys.forEach(apiKey => {
      if (!productData.categories[apiKey]) return;
      const categoryData = productData.categories[apiKey];
      if (typeof categoryData !== 'object' || Array.isArray(categoryData)) return;
      if (Object.keys(categoryData).length === 0) return;
      flattenCategoryData(categoryData).forEach(field =>
        fields.push({ ...field, apiKey, isEditable: isTabEditable ? field.isEditable : false })
      );
    });

    return sortFields(fields, tabId);
  }

  // ── Edit handlers ───────────────────────────────────────────────────────────
  const handleEditField = (field, nestedKey = null, arrayIndex = null) => {
    if (!field.isEditable) return;
    let currentValue = '';
    if (nestedKey) {
      if (arrayIndex !== null) {
        currentValue = field.arrayItems[arrayIndex].nestedFields.find(nf => nf.nestedKey === nestedKey)?.value || '';
      } else {
        currentValue = field.nestedFields.find(nf => nf.nestedKey === nestedKey)?.value || '';
      }
    } else {
      currentValue = field.value;
    }
    if (currentValue === 'N/A') currentValue = '';
    setEditingField({ apiKey: field.apiKey, fieldKey: field.fieldKey, nestedKey, arrayIndex, label: field.label });
    setEditValue(currentValue);
    setSaveNotification(null);
  };

  const handleCancelEdit = () => { setEditingField(null); setEditValue(''); };

  const handleSaveField = async () => {
    if (!editingField) return;
    try {
      setSaving(true);
      setSaveNotification(null);
      const { apiKey, fieldKey, nestedKey, arrayIndex } = editingField;
      const keyParts = fieldKey ? fieldKey.split('.') : [];
      const rootKey  = keyParts[0];
      const subKey   = keyParts[1] || null;
      let updatePayload = { categories: {} };

      let finalEditValue = editValue;
      try { const parsed = JSON.parse(editValue); if (typeof parsed === 'object') finalEditValue = parsed; } catch (e) {}

      const numericNestedKeys = ['width', 'height', 'length', 'volume', 'weight'];
      if (nestedKey && numericNestedKeys.includes(nestedKey)) {
        const num = Number(finalEditValue);
        if (!isNaN(num)) finalEditValue = num;
      }

      const booleanFieldKeys = [
        'pefCompliant', 'takeBackSchemeAvailable', 'plasticPartsMarked',
        'abcFactorApplied', 'isUhd', 'powerManagementFunctionPresent',
        'automaticBrightnessControlEnabled', 'warningBeforeStandby'
      ];
      const currentFieldKey = nestedKey || fieldKey;
      if (booleanFieldKeys.some(b => currentFieldKey?.includes(b))) {
        if (['true', true, 'Yes', 'yes'].includes(finalEditValue)) finalEditValue = true;
        else if (['false', false, 'No', 'no'].includes(finalEditValue)) finalEditValue = false;
      }

      if (nestedKey) {
        if (arrayIndex !== null) {
          const currentArray = subKey
            ? productData.categories[apiKey][rootKey][subKey]
            : productData.categories[apiKey][rootKey];
          const safeArray = Array.isArray(currentArray) && currentArray.every(item => typeof item === 'object' && !Array.isArray(item)) ? currentArray : [];
          const updatedArray = [...safeArray];
          updatedArray[arrayIndex] = { ...safeArray[arrayIndex], [nestedKey]: finalEditValue };
          updatePayload.categories[apiKey] = subKey
            ? { [rootKey]: { ...productData.categories[apiKey][rootKey], [subKey]: updatedArray } }
            : { [rootKey]: updatedArray };
        } else {
          const currentObj = subKey ? productData.categories[apiKey][rootKey][subKey] : productData.categories[apiKey][rootKey];
          updatePayload.categories[apiKey] = subKey
            ? { [rootKey]: { ...productData.categories[apiKey][rootKey], [subKey]: { ...currentObj, [nestedKey]: finalEditValue } } }
            : { [rootKey]: { ...currentObj, [nestedKey]: finalEditValue } };
        }
      } else {
        updatePayload.categories[apiKey] = subKey
          ? { [rootKey]: { ...productData.categories[apiKey][rootKey], [subKey]: finalEditValue } }
          : { [rootKey]: finalEditValue };
      }

      const response = await fetch(`${API_BASE_URL}/product/${productData.productId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updatePayload)
      });

      if (response.status === 401) { navigate('/login'); return; }
      if (!response.ok) throw new Error(`Failed to update: ${response.status}`);
      const result = await response.json();
      if (result.success) {
        setProductData({ ...result.data, editableCategories: productData.editableCategories });
        setSaveNotification({ type: 'success', message: 'Field updated successfully!' });
        setEditingField(null);
        setEditValue('');
      } else {
        throw new Error(result.message || 'Update failed');
      }
    } catch (err) {
      setSaveNotification({ type: 'error', message: err.message || 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const fieldBoxProps = { isEditMode, editingField, editValue, saving, setEditValue, onEdit: handleEditField, onSave: handleSaveField, onCancel: handleCancelEdit };

  // ── PDF generation ──────────────────────────────────────────────────────────
  const buildPDFTableData = (data, tableData, labelPrefix = '') => {
    Object.entries(data).forEach(([key, value]) => {
      const label = labelPrefix ? `${labelPrefix} › ${formatLabel(key)}` : formatLabel(key);
      if (key === 'physicalDimension' && isNestedObject(value)) {
        const parts = [];
        if (value.width)  parts.push(`Width: ${value.width}`);
        if (value.height) parts.push(`Height: ${value.height}`);
        if (value.length) parts.push(`Length: ${value.length}`);
        if (value.volume) parts.push(`Volume: ${value.volume}`);
        if (value.weight) parts.push(`Weight: ${value.weight}`);
        tableData.push([label, parts.join(' | ')]); return;
      }
      let parsedValue = tryParseJSON(value);
      if (parsedValue !== null) value = parsedValue;
      if (isArrayOfObjects(value)) {
        tableData.push([label, '']);
        value.forEach((item, index) => {
          tableData.push([`  Item ${index + 1}:`, '']);
          Object.entries(item).forEach(([nk, nv]) => tableData.push([`    • ${formatLabel(nk)}`, formatValue(nv)]));
        });
      } else if (isNestedObject(value)) {
        tableData.push([label, '']);
        buildPDFTableData(value, tableData, '  ');
      } else {
        tableData.push([label, formatValue(value)]);
      }
    });
  };

  const generatePDF = async () => {
    setDownloadingPDF(true);
    try {
      const jsPDF     = (await import('jspdf')).default;
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF();
      let y = 20;
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const margin = 20;
      const cw = pw - 2 * margin;
      const checkBreak = (h) => { if (y + h > ph - 20) { doc.addPage(); y = 20; } };

      doc.setFontSize(20); doc.setTextColor(37, 99, 235);
      doc.text('DIGITAL PRODUCT PASSPORT', pw / 2, y, { align: 'center' }); y += 15;
      doc.setFontSize(12); doc.setTextColor(100, 100, 100);
      doc.text(`DPP ID: ${dppId}`, pw / 2, y, { align: 'center' }); y += 6;
      doc.text(`Product ID: ${productData.productId}`, pw / 2, y, { align: 'center' }); y += 15;

      if (productData.productSummary) {
        const s = productData.productSummary;
        doc.setFontSize(14); doc.setTextColor(0, 0, 0);
        doc.text('Product Summary', margin, y); y += 8;
        autoTable(doc, {
          startY: y, head: [['Field', 'Value']],
          body: [['Product Name', s.productName || 'N/A'], ['Manufacturer', s.manufacturer || 'N/A'], ['Model Number', s.modelNumber || 'N/A'], ['Warranty', s.warranty || 'N/A']],
          theme: 'striped', headStyles: { fillColor: [37, 99, 235] }, margin: { left: margin, right: margin }, tableWidth: cw
        });
        y = doc.lastAutoTable.finalY + 15;
      }

      if (productData.categories) {
        const categoryGroups = {};
        Object.keys(productData.categories).forEach(k => {
          const tabId = apiToTabMapping[k];
          if (!tabId) return;
          if (!categoryGroups[tabId]) categoryGroups[tabId] = [];
          categoryGroups[tabId].push(k);
        });
        for (const [tabId, categoryKeys] of Object.entries(categoryGroups)) {
          checkBreak(30);
          const tabDef = allTabDefinitions[tabId];
          doc.setFontSize(14); doc.setTextColor(37, 99, 235);
          doc.text(tabDef?.label || formatLabel(tabId), margin, y); y += 8;
          const tableData = [];
          if (tabId === 'identification' && dppId) tableData.push(['DPP ID', dppId]);
          categoryKeys.forEach(k => {
            const catData = productData.categories[k];
            if (catData && typeof catData === 'object' && Object.keys(catData).length > 0) buildPDFTableData(catData, tableData);
          });
          if (tableData.length === 0) continue;
          autoTable(doc, {
            startY: y, head: [['Field', 'Value']], body: tableData,
            theme: 'grid', headStyles: { fillColor: [229, 231, 235], textColor: [55, 65, 81] },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: { 0: { cellWidth: cw * 0.4, fontStyle: 'bold' }, 1: { cellWidth: cw * 0.6 } },
            margin: { left: margin, right: margin }, tableWidth: cw,
            didDrawCell: (data) => {
              if (data.column.index === 1 && data.cell.section === 'body') {
                const v = data.cell.text[0];
                if (v && isURL(v)) doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: v.startsWith('www.') ? `https://${v}` : v });
              }
            }
          });
          y = doc.lastAutoTable.finalY + 10;
        }
      }

      y = ph - 30;
      doc.setFontSize(8); doc.setTextColor(150, 150, 150);
      doc.text(`Generated on ${new Date().toLocaleString()}`, pw / 2, y, { align: 'center' });
      doc.text('Digital Product Passport Report', pw / 2, y + 5, { align: 'center' });
      doc.save(`Product_Passport_${dppId}_${Date.now()}.pdf`);
    } catch (err) {
      console.error('PDF Generation Error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // ── Metric card ─────────────────────────────────────────────────────────────
  const MetricCard = ({ label, value, iconSrc, iconBg }) => (
    <div
      className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 cursor-pointer hover:shadow-lg transition-all duration-300"
      onClick={() => setIsRightSidebarExpanded(!isRightSidebarExpanded)}
    >
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <div className="flex-1">
          <p className="text-xs text-gray-600 mb-1">{label}</p>
          <p className={`${isRightSidebarExpanded ? 'text-base sm:text-lg' : 'text-sm'} font-semibold text-gray-900 break-words transition-all duration-300`}>
            {value}
          </p>
        </div>
        <div className={`${isRightSidebarExpanded ? 'w-10 h-10' : 'w-8 h-8'} ${iconBg} rounded-lg flex items-center justify-center ml-2 flex-shrink-0 transition-all duration-300`}>
          <img src={iconSrc} alt="" className={`${isRightSidebarExpanded ? 'w-5 h-5' : 'w-4 h-4'} object-contain transition-all duration-300`} />
        </div>
      </div>
    </div>
  );

  // ── Loading / Error / Empty ─────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EDEDED' }}>
      <div className="text-center">
        <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading product passport...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EDEDED' }}>
      <div className="text-center max-w-md mx-auto p-6">
        <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Product Passport</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button onClick={() => navigate('/product-data')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Go Back</button>
      </div>
    </div>
  );

  if (!productData) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EDEDED' }}>
      <p className="text-gray-600">No product data available</p>
    </div>
  );

  const { productSummary } = productData;

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen overflow-y-auto h-full" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {saveNotification && (
        <div className="fixed top-20 right-4 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${saveNotification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
            {saveNotification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            <span className="text-sm font-medium">{saveNotification.message}</span>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════ MOBILE VIEW ══ */}
      <div className="lg:hidden">
        <div className="bg-white px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => navigate('/product-data')} className="p-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-blue-600 font-bold text-sm">DIGITAL PRODUCT PASSPORT</h1>
            <button
              onClick={() => { setIsEditMode(!isEditMode); setEditingField(null); setSaveNotification(null); }}
              className={`p-2 rounded-lg transition-colors ${isEditMode ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}
            >
              {isEditMode ? <Eye className="w-5 h-5" /> : <Edit2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {isEditMode && (
          <div className="bg-blue-100 border-b border-blue-300 px-4 py-2">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <p className="text-xs text-blue-900 font-medium">
                {(productData?.editableCategories?.length || 0) > 0
                  ? 'Edit Mode Active – Pencil icons appear only on your permitted categories'
                  : 'Edit Mode Active – No categories are assigned edit access for your role'}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white mx-4 mt-4 rounded-lg border-2 border-dashed border-blue-300 p-4">
          <img src={productSummary?.image || '/Tv.png'} alt={productSummary?.productName || 'Product'} className="w-full h-48 object-contain" />
        </div>

        <div className="bg-white mx-4 mt-4 rounded-lg border border-blue-300 p-3">
          <p className="text-blue-600 font-bold text-center text-sm">ID: {dppId}</p>
        </div>

        <div className="px-4 mt-4 grid grid-cols-2 gap-3">
          {[
            { label: 'Product Name', value: productSummary?.productName, icon: '/metricicon1.png', bg: 'bg-green-100', trend: '↑ 3.5%', color: 'text-green-600' },
            { label: 'Manufacturer', value: productSummary?.manufacturer, icon: '/metricicon2.png', bg: 'bg-yellow-100', trend: '↓ 1.3%', color: 'text-red-600' },
            { label: 'Product ID', value: productData.productId, icon: '/metricicon3.png', bg: 'bg-red-100', trend: '↑ 4.3%', color: 'text-green-600' },
            { label: 'Warranty', value: productSummary?.warranty, icon: '/metricicon4.png', bg: 'bg-orange-100', trend: '↑ 1.8%', color: 'text-green-600' },
          ].map(({ label, value, icon, bg, trend, color }) => (
            <div key={label} className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-600">{label}</p>
                <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center`}>
                  <img src={icon} alt="" className="w-4 h-4" />
                </div>
              </div>
              <p className="text-sm font-semibold text-gray-900">{value || 'N/A'}</p>
              <p className={`text-xs ${color} mt-1`}>{trend} Up from past week</p>
            </div>
          ))}
        </div>

        {/* Categories Accordion — mobile */}
        <div className="px-4 mt-6 mb-24 space-y-3">
          {tabs.map(tab => {
            const IconComponent = tab.icon;
            const isExpanded    = expandedCategories[tab.id];
            const isTraceability = tab.id === 'traceabilityEvents';
            const fields        = isTraceability ? [] : getFieldsForTab(tab.id);

            return (
              <div key={tab.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setExpandedCategories(prev => ({ ...prev, [tab.id]: !prev[tab.id] }))}
                  className="w-full flex items-center justify-between p-4 bg-blue-50"
                >
                  <div className="flex items-center gap-2">
                    <IconComponent className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-800">{tab.label}</span>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
                </button>
                {isExpanded && (
                  <div className="p-4">
                    {isTraceability
                      ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                          <TraceabilityTimeline
                            dppId={dppId}
                            transactionalData={transactionalData}
                            visibleFields={visibleFields}
                          />
                          <LifecycleEventsList
                            dppId={dppId}
                            userRole={userRole}
                          />
                        </div>
                      )
                      : fields.length === 0
                        ? <p className="text-sm text-gray-400 text-center py-2">No data available</p>
                        : <div className="space-y-3">{fields.map((field, i) => <FieldBox key={i} field={field} {...fieldBoxProps} />)}</div>
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 z-50">
          <button
            onClick={generatePDF}
            disabled={downloadingPDF}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {downloadingPDF ? <Loader className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            Download
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 bg-white text-blue-600 border-2 border-blue-600 px-4 py-3 rounded-lg font-medium hover:bg-blue-50">
            <Share2 className="w-5 h-5" />
            Share
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════ DESKTOP VIEW ══ */}
      <div className="hidden lg:block">
        {/* Header */}
        <div className="px-4 mt-4 sm:px-6 lg:px-8 pt-3 sm:pt-4 pb-3 sm:pb-4 mx-2 sm:mx-4 mb-2 sm:mb-4 rounded-xl overflow-hidden shadow bg-white">
          <div className="flex items-center justify-between relative">
            <button onClick={() => navigate('/product-data')} className="flex items-center text-blue-600 hover:text-blue-700 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Back</span>
            </button>

            <div className="absolute left-1/2 transform -translate-x-1/2">
              <span className="text-blue-600 font-medium text-xs sm:text-sm lg:text-base">DPP ID: {dppId}</span>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => { setIsEditMode(!isEditMode); setEditingField(null); setSaveNotification(null); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${isEditMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {isEditMode
                  ? <><Eye className="w-4 h-4" /><span className="hidden sm:inline">View Mode</span></>
                  : <><Edit2 className="w-4 h-4" /><span className="hidden sm:inline">Edit Mode</span></>
                }
              </button>
              <button
                onClick={generatePDF}
                disabled={downloadingPDF}
                className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm disabled:opacity-50 transition-colors"
                title="Download PDF Report"
              >
                {downloadingPDF
                  ? <Loader className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 animate-spin" />
                  : <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                }
              </button>
              <button className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {isEditMode && (
          <div className="px-4 sm:px-6 lg:px-8 mx-2 sm:mx-4 mb-4">
            <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 flex items-center gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-blue-900 font-medium">Edit Mode Active</p>
                <p className="text-xs text-blue-700">
                  {(productData?.editableCategories?.length || 0) > 0
                    ? `You can edit ${productData.editableCategories.length} category(s). Pencil icons appear only on your permitted fields.`
                    : 'No categories are assigned edit access for your role.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="px-2 sm:px-4 lg:px-8 pb-4 sm:pb-6 h-auto lg:h-[calc(100vh-180px)]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full">

            {/* Left Sidebar */}
            <div className="lg:col-span-2 space-y-2 overflow-y-auto h-full pr-2 hide-scrollbar">
              {tabs.map(tab => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
                    }`}
                  >
                    <IconComponent className="w-5 h-5 flex-shrink-0" />
                    <span className="text-xs font-medium leading-tight">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Main Content */}
            <div className={`col-span-1 ${isRightSidebarExpanded ? 'lg:col-span-7' : 'lg:col-span-8'} bg-white rounded-lg p-4 sm:p-6 shadow-sm overflow-y-auto lg:h-full transition-all duration-300 light-scrollbar`}>
              {activeTab === 'traceabilityEvents' ? (
                // ── Desktop: pass transactionalData + visibleFields ──
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <TraceabilityTimeline
                    dppId={dppId}
                    transactionalData={transactionalData}
                    visibleFields={visibleFields}
                  />
                  <LifecycleEventsList
                    dppId={dppId}
                    userRole={userRole}
                  />
                </div>
              ) : (() => {
                const fields = getFieldsForTab(activeTab);
                if (fields.length === 0) return (
                  <div className="flex items-center justify-center h-48">
                    <p className="text-gray-400 text-sm">No data available for this category</p>
                  </div>
                );
                const left  = fields.filter((_, i) => i % 2 === 0);
                const right = fields.filter((_, i) => i % 2 === 1);
                return (
                  <div className="flex gap-4 items-start">
                    <div className="flex-1 flex flex-col gap-3">
                      {left.map((field, i) => <FieldBox key={i} field={field} {...fieldBoxProps} />)}
                    </div>
                    <div className="flex-1 flex flex-col gap-3">
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4">
                        <img src={productSummary?.image || '/Tv.png'} alt={productSummary?.productName || 'Product'} className="h-48 object-contain rounded-lg" />
                      </div>
                      {right.map((field, i) => <FieldBox key={i} field={field} {...fieldBoxProps} />)}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Right Sidebar */}
            <div className={`${isRightSidebarExpanded ? 'lg:col-span-3' : 'lg:col-span-2'} space-y-4 overflow-y-auto h-full transition-all duration-300 hide-scrollbar`}>
              <MetricCard label="Product Name" value={productSummary?.productName || 'N/A'} iconSrc="/metricicon1.png" iconBg="bg-green-100" />
              <MetricCard label="Manufacturer"  value={productSummary?.manufacturer  || 'N/A'} iconSrc="/metricicon2.png" iconBg="bg-yellow-100" />
              <MetricCard label="Model Number"  value={productSummary?.modelNumber   || 'N/A'} iconSrc="/metricicon3.png" iconBg="bg-red-100" />
              <MetricCard label="Warranty"      value={productSummary?.warranty      || 'N/A'} iconSrc="/metricicon4.png" iconBg="bg-orange-100" />
            </div>

          </div>
        </div>
      </div>

      <style>{`
        .hide-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .light-scrollbar::-webkit-scrollbar { width: 6px; }
        .light-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .light-scrollbar::-webkit-scrollbar-thumb { background: #888; border-radius: 10px; }
        .light-scrollbar::-webkit-scrollbar-thumb:hover { background: #555; }
      `}</style>
    </div>
  );
}

export default ProductPassport;